import * as React from "react";
import clone from "clone";
import { SurveyScore, stringUtil } from "../../../../../../../amorg-common";
import { Button } from "../../../../../../../designSystem/button/Button";
import { colors } from "../../../../../../../designSystem/colors/color";
import { Flex } from "../../../../../../../designSystem/compositeComponents/FlexHelpers";
import { StatusColorPopupMenuButton } from "../../../../../../../designSystem/compositeComponents/StatusColorPopupMenuButton";
import { OnOff } from "../../../../../../../designSystem/inputs/OnOff";
import { TextInput } from "../../../../../../../designSystem/inputs/TextInput";
import { ErrorLabel, Label } from "../../../../../../../designSystem/Label";
import { ToggletipTheOne } from "../../../../../../../designSystem/ToggletipTheOne";
import { PopupMenuButton } from "../../../../../../../designSystem/compositeComponents/PopupMenuButton";

type ScoredQuestionOptionEditorDraft = {
    scored:true,
    options:{
        originalIndex: number | null,
        text: string,
        value: string,
        score: SurveyScore
    }[]
};
type UnscoredQuestionOptionEditorDraft = {
    scored:false,
    options:{
        originalIndex: number | null,
        text: string,
        value: string
    }[]
};

export type QuestionOptionEditorDraft = ScoredQuestionOptionEditorDraft | UnscoredQuestionOptionEditorDraft;

type CustomQuestionOptionEditorProps = {
    disabled?: boolean,
    hideScoringToggle?: boolean,
    draft: QuestionOptionEditorDraft,
    onChange: (config: QuestionOptionEditorDraft) => void,
};

export function CustomQuestionOptionEditor(props: CustomQuestionOptionEditorProps): JSX.Element
{
    function handleScoringToggle(active: boolean)
    {
        if (active)
        {
            props.onChange({
                scored: true,
                options: props.draft.options.map((option) => ({
                    originalIndex: option.originalIndex,
                    value: option.value,
                    text: option.text,
                    score: 3 as SurveyScore
                }))
            });
        }
        else
        {
            props.onChange({
                scored: false,
                options: props.draft.options.map((option) => ({
                    originalIndex: option.originalIndex,
                    value: option.value,
                    text: option.text
                }))
            })
        }
    }

    function handleOptionTextChange(index: number, text: string)
    {
        const copy = clone(props.draft);
        copy.options[index].text = text;
        
        props.onChange(copy);
    }

    function handleOptionScoreChange(index: number, score: number)
    {
        if (!props.draft.scored)
        {
            throw new Error("Cannot change option score when scoring is disabled");
        }

        const copy = clone(props.draft);
        copy.options[index].score = score as SurveyScore;

        props.onChange(copy);
    }

    function handleAddOption()
    {
        const value = stringUtil.generateNextSequenceValue('option_', props.draft.options.map(o => o.value), '');
        const name = stringUtil.generateNextSequenceValue('Svarmulighed ', props.draft.options.map(o => o.text), '');

        const copy:QuestionOptionEditorDraft = clone(props.draft);
        if(copy.scored)
        {
            props.onChange({
                scored: true,
                options: [
                    ...props.draft.options.map((option) => ({
                        originalIndex: option.originalIndex,
                        value: option.value,
                        text: option.text,
                        score: option.score
                    })),
                    {
                        originalIndex: null,
                        value,
                        text: name,
                        score: 3 as SurveyScore
                    }
                ]
            });
        }
        else
        {
            props.onChange({
                scored: false,
                options: [
                    ...props.draft.options.map((option) => ({
                        originalIndex: option.originalIndex,
                        value: option.value,
                        text: option.text
                    })),
                    {
                        originalIndex: null,
                        value,
                        text: name
                    }
                ]
            });
        }
    }

    function handleRemoveOption(index: number)
    {
        if (props.draft.options.length <= 2)
        {
            return;
        }

        const copy = clone(props.draft);
        copy.options.splice(index, 1);

        props.onChange(copy);
    }

    function handleSelectPreset(presetValue:string)
    {
        const preset = presets.find(p => p.value === presetValue);
        if (preset)
        {
            if(props.draft.scored)
            {
                props.onChange(preset.draft);
            }
            else {
                props.onChange({
                    scored: false,
                    options: preset.draft.options.map(option => ({
                        originalIndex: option.originalIndex,
                        value: option.value,
                        text: option.text
                    }))
                });
            }
        }
    }

    const identicalOptionsTexts = props.draft.options.some(option => option.text && props.draft.options.filter(o => o.text === option.text).length > 1);

    return (
        <Flex flexDirection="column" gap={8}>
            {!props.hideScoringToggle &&
                <Flex justifyContent="space-between" alignItems="center" gap={8}>
                    <Flex gap={8} alignItems="center">
                        <span>Bidrager til score</span>
                        <ToggletipTheOne>
                            <span>Når spørgsmålet bidrager til scoren, skal valgmulighedernes værdi angives på en skala fra &quot;Meget ringe&quot; til &quot;Meget god&quot;.</span>
                        </ToggletipTheOne>
                        <OnOff active={props.draft.scored} onClick={handleScoringToggle} disabled={props.disabled} />
                    </Flex>
                    <PopupMenuButton
                        buttonText="vælg fra skabelon"
                        buttonIcon="list"
                        entries={presets.map(preset => ({ text: preset.text, value: preset.value, type:'menuItem', onClick:() => handleSelectPreset(preset.value) }))}
                    />
                </Flex>
            }
            <Label>Svarmuligheder</Label>
            <small>Minimum 2 svarmuligheder. Maksimum 10 svarmuligheder.</small>
            {identicalOptionsTexts && <ErrorLabel>Der er duplikerede svarmuligheder. Hver svarmulighed skal være unik.</ErrorLabel>}
            {props.draft.options.map((option, index) => (
                <Flex key={`${option.value}-${index}`} gap={8} alignItems="center">
                    <div style={{ flexGrow: 1 }}>
                        <TextInput
                            value={option.text}
                            onChange={(value) => handleOptionTextChange(index, value)}
                            disabled={props.disabled} />
                    </div>
                    {props.draft.scored &&
                        <StatusColorPopupMenuButton
                            options={[
                                { text: "Meget ringe", value: "1", color: colors.graphs.survey.score.score1 },
                                { text: "Ringe", value: "2", color: colors.graphs.survey.score.score2 },
                                { text: "Neutral", value: "3", color: colors.graphs.survey.score.score3 },
                                { text: "God", value: "4", color: colors.graphs.survey.score.score4 },
                                { text: "Meget god", value: "5", color: colors.graphs.survey.score.score5 }
                            ]}
                            selectedValue={`${option.score}`}
                            onChange={(value) => handleOptionScoreChange(index, parseInt(value, 10))}
                            disabled={props.disabled}
                        />
                    }
                        <Button variant="text" onClick={() => handleRemoveOption(index)} text="Fjern" disabled={props.disabled || props.draft.options.length <= 2} />
                </Flex>
            ))}
            <Button variant="text" onClick={handleAddOption} text="Tilføj svarmulighed" disabled={props.disabled} />
        </Flex>
    );
}


type OptionPreset = {
    text: string,
    value: string
    draft:ScoredQuestionOptionEditorDraft
};


const presets:OptionPreset[] = [
    {
        text: "I hvor høj grad",
        value: "low-to-high",
        draft: {
            scored: true,
            options: [
                { text: "I meget lav grad", value: "low", score: 1, originalIndex:null },
                { text: "I lav grad", value: "medium-low", score: 2, originalIndex:null },
                { text: "Delvist", value: "medium", score: 3, originalIndex:null },
                { text: "I høj grad", value: "medium-high", score: 4, originalIndex:null },
                { text: "I meget høj grad", value: "high", score: 5, originalIndex:null }
            ]
        }
    },
    {
        text: "Hvor ofte",
        value: "frequency",
        draft: {
            scored: true,
            options: [
                { text: "Aldrig / næsten aldrig", value: "never", score: 1, originalIndex:null },
                { text: "Sjældent", value: "rarely", score: 2, originalIndex:null },
                { text: "Somme tider", value: "sometimes", score: 3, originalIndex:null },
                { text: "Ofte", value: "often", score: 4, originalIndex:null },
                { text: "Altid", value: "always", score: 5, originalIndex:null }
            ]
        }
    },
    {
        text: "Er det sket",
        value: "occurred",
        draft: {
            scored: true,
            options: [
                { text: "Ja, dagligt eller næsten dagligt", value: "daily", score: 1, originalIndex:null },
                { text: "Ja, ugentligt", value: "weekly", score: 2, originalIndex:null },
                { text: "Ja, månedligt", value: "monthly", score: 3, originalIndex:null },
                { text: "Ja, sjældnere", value: "rarely", score: 4, originalIndex:null },
                { text: "Nej", value: "never", score: 5, originalIndex:null }
            ]
        }
    },
    {
        text: "Tilfredshed 1-10",
        value: "satisfaction-1-10",
        draft: {
            scored: true,
            options: [
                { text: "1", value: "1", score: 1, originalIndex:null },
                { text: "2", value: "2", score: 1, originalIndex:null },
                { text: "3", value: "3", score: 2, originalIndex:null },
                { text: "4", value: "4", score: 2, originalIndex:null },
                { text: "5", value: "5", score: 3, originalIndex:null },
                { text: "6", value: "6", score: 3, originalIndex:null },
                { text: "7", value: "7", score: 4, originalIndex:null },
                { text: "8", value: "8", score: 4, originalIndex:null },
                { text: "9", value: "9", score: 5, originalIndex:null },
                { text: "10", value: "10", score: 5, originalIndex:null }
            ]
        }
    }
]